import React, { useEffect, useState, ChangeEvent } from "react";
import TrainingPeriodListItem from "./TrainingPeriodListItem";
import TrainingListLabelInput from "../training/TrainingListLabelInput";
import trainingPeriodService from "../../services/trainingPeriodService";
import { useNavigate } from "react-router-dom";
import TrainingPeriod from "../../types/TrainingPeriod"
import { TimePercent } from "../../types/SummaryDTO";

function TrainingPeriodList() {
  const navigate = useNavigate();

  const [listingDates, setListingDates] = useState({
    listSince: new Date(new Date().getFullYear(), 0, 1), // Start of year
    listTo: new Date(), // Today
  });

  const [trainingPeriods, setTrainingPeriods] = useState<TrainingPeriod[]>([]);

  useEffect(() => {
    loadTrainingPeriods();
  }, [listingDates]);

  const loadTrainingPeriods = async () => {
    try {
      const periods = await trainingPeriodService.getByDateForRange(
        listingDates.listSince,
        listingDates.listTo
      );
      setTrainingPeriods(periods);
    } catch (error) {
      console.error("Error loading training periods:", error);
    }
  };

  const handleListingDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target; // Use event.target safely
    if (value === "") return;
  
    setListingDates((prev) => ({
      ...prev,
      [id.includes("Since") ? "listSince" : "listTo"]: new Date(value), // Adjust date value
    }));
  };
  const handleAddTrainingPeriod = () => {
    navigate("/training-periods/add");
  };

  const formatDateLocal = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const formatTime = (seconds?: number): string => {
    const s = seconds ?? 0;
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatPct = (pct?: number): string => {
    const p = pct ?? 0;
    return `${p.toFixed(1)}%`;
  };

  // Strefy: G1..G4 rosnąco, potem numery 1..6, potem reszta alfabetycznie (Z itp)
const sortHrZoneKeys = (a: string, b: string): number => {
  const rank = (z: string): [number, number | string] => {
    if (z === "G0" || z === "0") return [10, z]; // zwykle pomijane w domenach, ale w strefach mogą się pojawić
    if (z.startsWith("G")) {
      const n = Number(z.slice(1));
      if (!Number.isNaN(n)) return [1, n];
    }
    const n = Number(z);
    if (!Number.isNaN(n)) return [2, n];
    return [9, z];
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra[0] !== rb[0]) return ra[0] - rb[0];
  if (typeof ra[1] === "number" && typeof rb[1] === "number") return ra[1] - rb[1];
  return String(ra[1]).localeCompare(String(rb[1]));
};

// Domeny: ustawiamy kolejność biznesową
const DOMAIN_ORDER: string[] = ["MODERATE", "HEAVY", "SEVERE", "EXTREME", "STRENGTH"];
const sortDomainKeys = (a: string, b: string): number => {
  const ia = DOMAIN_ORDER.indexOf(a);
  const ib = DOMAIN_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
};

const renderTimePercentList = (
  data: Record<string, TimePercent> | undefined,
  sortFn: (a: string, b: string) => number
) => {
  if (!data || Object.keys(data).length === 0) return <span className="text-muted">—</span>;

  const keys = Object.keys(data).sort(sortFn);

  return (
    <div className="d-flex flex-column">
      {keys.map((k) => (
        <div key={k} className="d-flex justify-content-between">
          <span className="mr-2">{k}</span>
          <span className="text-nowrap">
            {formatTime(data[k]?.timeSeconds)} ({formatPct(data[k]?.percent)})
          </span>
        </div>
      ))}
    </div>
  );
};

  return (
    <div className="card my-2">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>Okresy treningowe</div>
        <div className="d-flex">
          <TrainingListLabelInput
            label="Od:"
            id="trainingPeriodListSince"
            type="date"
            value={formatDateLocal(listingDates.listSince)}
            onChange={handleListingDate}
          />
          <TrainingListLabelInput
            label="Do:"
            id="trainingPeriodListTo"
            type="date"
            value={formatDateLocal(listingDates.listTo)}
            onChange={handleListingDate}
          />
          <button
            type="button"
            className="btn btn-success ml-2"
            onClick={handleAddTrainingPeriod}
          >
            Dodaj
          </button>
        </div>
      </div>
      <div className="card-body">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Rodzaj</th>
              <th>Początek</th>
              <th>Koniec</th>
              <th>Czas</th>
              <th>Dystans</th>
              <th>Przewyższenie</th>
              <th>Strefy HR</th>
              <th>Domeny</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {trainingPeriods.length > 0 ? (
              trainingPeriods.map((period) => (
                <TrainingPeriodListItem
                  key={period.id}
                  trainingPeriod={period}
                  onPeriodDeleted={loadTrainingPeriods}
                />
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">
                  Brak okresów treningowych.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TrainingPeriodList;
